/**
 * Quota — one ticket type's entire inventory in one document. THREE
 * server-txn writers: settleOrder (+, sale and guest alike), the refund
 * consumer (−), and the capture-hard-fail compensating transaction (−) —
 * each re-checks its own precondition against the live type+quota docs
 * inside its own transaction. `available = capacity − consumed`, computed
 * on read — capacity lives on the parent TicketType doc, never mirrored
 * here.
 *
 * Path: `workspaces/{orgId}/events/{eventId}/ticketTypes/{typeId}/quota/inventory`
 * — ONE doc per ticket type. Member read behind `tickets.reports.view`
 * (mfa-gated); NO client write path exists at all — the three server
 * transactions above are the sole writers.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import type { Actor } from "./workspace.entity.ts";

export const QuotaSchema = z
	.object({
		consumed: z
			.number()
			.int()
			.min(0)
			.describe(
				"consumed count — capacity and price are read live from the parent ticket type’s own document inside the settle transaction, never mirrored here; available = capacity − consumed, computed on read",
			),
		updatedAt: z.custom<Timestamp>(),
	})
	.describe(
		"one ticket type’s entire inventory — three server transactions write it (settle, refund, capture-hard-fail compensation), each re-checking its own precondition; members read it; nothing else ever touches it",
	);
export type Quota = z.infer<typeof QuotaSchema>;

export const Quota = entity<Actor>()({
	schema: QuotaSchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/ticketTypes/{typeId}/quota/inventory",
		}),
	);
